'use client'

import { memo, useId, useSyncExternalStore } from 'react'
import { AlertCircle, Check, CheckCheck, Loader2, Mic, Pause, Play, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PlaybackClock } from '@/hooks/use-santa-voice-player'
import type { ChatEntry, Delivery } from '@/lib/santa-chat/chat-state'
import { formatPlaybackPosition, formatVoiceDuration } from '@/lib/santa-chat/voice-duration'

export interface VoiceNoteBubbleLabels {
  listen: string
  pause: string
  loadingVoice: string
  flaggedNote: string
  resend: string
}

interface VoiceNoteBubbleProps {
  entry: ChatEntry
  labels: VoiceNoteBubbleLabels
  playing: boolean
  /** Su voz se está pidiendo al servidor. */
  loading: boolean
  clock: PlaybackClock
  /** Con la nota sonando, el mismo botón la pausa. */
  onPlay: (entry: ChatEntry) => void
  /** Reenvía una nota fallida del niño (`entry.retryable`). */
  onResend: (localId: string) => void
  /** Papá Noel está contestando o se acabó el día: el reenvío espera. */
  resendDisabled: boolean
}

/** Alturas fijas de la «onda»: decorativa, igual en todas las notas. */
const WAVE = [40, 70, 55, 90, 65, 35, 80, 60, 95, 50, 75, 45, 85, 55, 30, 65].map((height, position) => ({
  id: `bar-${position}`,
  height,
}))

/** Sin progreso, todas iguales; con él, escuchadas encendidas y el resto apagadas. */
function barOpacity(index: number, progress: number | null): string {
  if (progress === null) return 'opacity-60'
  return index < progress * WAVE.length ? 'opacity-100' : 'opacity-30'
}

/**
 * Con `progress` (0–1) las barras ya escuchadas se encienden, como en una
 * mensajería: quien no sabe leer ve que la nota avanza. Sin él, `active` pulsa
 * el contenedor entero (un pulso en vez de 16: las barras van en fase).
 */
function Waveform({ active, progress = null }: { active: boolean; progress?: number | null }) {
  const pulsing = active && progress === null
  return (
    <span aria-hidden className={cn('flex h-8 flex-1 items-center gap-0.5', pulsing && 'motion-safe:animate-pulse')}>
      {WAVE.map(({ id, height }, index) => (
        <span
          key={id}
          style={{ height: `${height}%` }}
          className={cn('w-1 rounded-full bg-current transition-opacity', barOpacity(index, progress))}
        />
      ))}
    </span>
  )
}

/** «0:07» junto a la onda; nada si la duración no se conoce. */
function Duration({ seconds }: { seconds: number | undefined }) {
  const label = formatVoiceDuration(seconds)
  if (!label) return null
  return <span className="readable-body shrink-0 text-xs tabular-nums opacity-80">{label}</span>
}

function DeliveryTick({ delivery }: { delivery: Delivery }) {
  if (delivery === 'failed') return <AlertCircle aria-hidden className="size-4 text-destructive" />
  if (delivery === 'sending') return <Check aria-hidden className="size-4 opacity-70" />
  return <CheckCheck aria-hidden className="size-4 text-accent" />
}

/** Lo que se ve mientras suena: la onda se rellena y el contador avanza («0:03»). */
function PlayingProgress({ entry, clock }: Pick<VoiceNoteBubbleProps, 'entry' | 'clock'>) {
  const progress = useSyncExternalStore(clock.subscribe, clock.read, clock.read)
  // Antes de la voz suena la risa del gesto: la nota aún está en 0:00.
  const mine = progress.id === entry.id
  const elapsed = mine ? progress.elapsedSeconds : 0
  const duration = mine && Number.isFinite(progress.durationSeconds) ? progress.durationSeconds : entry.durationSeconds
  const fraction = duration ? Math.min(1, elapsed / duration) : 0
  return (
    <>
      <Waveform active progress={fraction} />
      <span className="readable-body shrink-0 text-xs font-semibold tabular-nums">{formatPlaybackPosition(elapsed)}</span>
    </>
  )
}

function PlayIcon({ playing, loading }: { playing: boolean; loading: boolean }) {
  if (loading) return <Loader2 aria-hidden className="size-5 motion-safe:animate-spin" />
  if (playing) return <Pause aria-hidden className="size-5" />
  return <Play aria-hidden className="size-5" />
}

function playLabel(labels: VoiceNoteBubbleLabels, playing: boolean, loading: boolean): string {
  if (loading) return labels.loadingVoice
  return playing ? labels.pause : labels.listen
}

function SantaNote({ entry, labels, playing, loading, clock, onPlay }: VoiceNoteBubbleProps) {
  // The note's text describes its ▶: a screen reader hears which message the button plays.
  const textId = useId()
  return (
    <div
      className={cn(
        'max-w-[85%] self-start rounded-2xl rounded-tl-sm bg-card px-3 py-2 text-card-foreground shadow transition-shadow',
        (playing || loading) && 'ring-2 ring-primary'
      )}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={playLabel(labels, playing, loading)}
          aria-describedby={textId}
          aria-busy={loading}
          onClick={() => onPlay(entry)}
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-90"
        >
          <PlayIcon playing={playing} loading={loading} />
        </button>
        {playing ? (
          <PlayingProgress entry={entry} clock={clock} />
        ) : (
          <>
            <Waveform active={loading} />
            <Duration seconds={entry.durationSeconds} />
          </>
        )}
      </div>
      <p id={textId} className="readable-body mt-1 text-xs text-muted-foreground">
        {entry.text}
      </p>
    </div>
  )
}

type ChildNoteProps = Pick<VoiceNoteBubbleProps, 'entry' | 'labels' | 'onResend' | 'resendDisabled'>

/** Botón grande con flecha circular: el ⚠️ solo no le dice al niño qué hacer. */
function ResendButton({ entry, labels, onResend, resendDisabled }: ChildNoteProps) {
  return (
    <button
      type="button"
      onClick={() => onResend(entry.id)}
      disabled={resendDisabled}
      className="readable-body flex min-h-11 items-center gap-2 rounded-full bg-destructive px-4 text-sm font-semibold text-destructive-foreground shadow transition-transform active:scale-95 disabled:opacity-50"
    >
      <RotateCcw aria-hidden className="size-5" />
      {labels.resend}
    </button>
  )
}

function ChildNote(props: ChildNoteProps) {
  const { entry, labels } = props
  const isVoice = entry.inputMode === 'voice'
  const failed = entry.delivery === 'failed'
  return (
    <div className="flex max-w-[85%] flex-col items-end gap-1 self-end">
      <div
        className={cn(
          'rounded-2xl rounded-tr-sm bg-secondary px-3 py-2 text-secondary-foreground shadow',
          failed && 'opacity-70 ring-2 ring-destructive'
        )}
      >
        <div className="flex items-center gap-2">
          {isVoice && <Mic aria-hidden className="size-5 shrink-0" />}
          {isVoice ? <Waveform active={false} /> : <span className="readable-body">{entry.text}</span>}
          {isVoice && <Duration seconds={entry.durationSeconds} />}
          <DeliveryTick delivery={entry.delivery} />
        </div>
        {isVoice && entry.text && <p className="readable-body mt-1 text-xs opacity-80">{entry.text}</p>}
      </div>
      {failed && entry.retryable && <ResendButton {...props} />}
      {entry.flagged && <p className="readable-body text-xs text-muted-foreground">{labels.flaggedNote}</p>}
    </div>
  )
}

/**
 * Burbuja de nota de voz al estilo de una mensajería: Papá Noel a la izquierda,
 * el niño a la derecha. Memoizada: cada `dispatch` de la pantalla no repinta
 * todo el historial, solo la burbuja que cambia.
 */
export const VoiceNoteBubble = memo(function VoiceNoteBubble(props: VoiceNoteBubbleProps) {
  if (props.entry.author === 'santa') return <SantaNote {...props} />
  return <ChildNote {...props} />
})
